import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Trash2, Search, RefreshCw, Eye, X, ChevronLeft, ChevronRight,
  User, Phone, Mail, Calendar, Globe, Smartphone, Monitor,
  AlertTriangle, UserX, SlidersHorizontal, ChevronDown,
} from "lucide-react";
import { deletedUsersApi } from "../../services/api";
import { showToast } from "../../utils/toast";

/* ── Helpers ─────────────────────────────────────────────── */
function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}
function fmtDateTime(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
}
function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

/* ── Badge ───────────────────────────────────────────────── */
function Badge({ children, variant = "default" }) {
  const styles = {
    default:  { bg: "rgba(15,61,62,0.15)",   color: "#0F3D3E",   border: "rgba(15,61,62,0.3)" },
    success:  { bg: "rgba(34,197,94,0.1)",   color: "#16a34a",   border: "rgba(34,197,94,0.3)" },
    danger:   { bg: "rgba(239,68,68,0.1)",   color: "#dc2626",   border: "rgba(239,68,68,0.3)" },
    warning:  { bg: "rgba(245,158,11,0.1)",  color: "#d97706",   border: "rgba(245,158,11,0.3)" },
    blue:     { bg: "rgba(59,130,246,0.1)",  color: "#2563eb",   border: "rgba(59,130,246,0.3)" },
    purple:   { bg: "rgba(167,139,250,0.1)", color: "#7c3aed",   border: "rgba(167,139,250,0.3)" },
  };
  const s = styles[variant] || styles.default;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

/* ── Source badge ────────────────────────────────────────── */
function SourceBadge({ source }) {
  if (!source) return <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>;
  const v = source === "ASTRO" ? "warning" : source === "IND" ? "blue" : "default";
  return <Badge variant={v}>{source}</Badge>;
}

/* ── Device badge ────────────────────────────────────────── */
function DeviceBadge({ type }) {
  if (!type) return <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>;
  const icon = type === "ANDROID" ? <Smartphone size={11} /> : <Monitor size={11} />;
  return (
    <Badge variant={type === "ANDROID" ? "success" : "blue"}>
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>{icon}{type}</span>
    </Badge>
  );
}

/* ── Select dropdown ─────────────────────────────────────── */
function Select({ value, onChange, options, placeholder, disabled }) {
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          appearance: "none", padding: "8px 32px 8px 12px",
          border: "1px solid var(--border)", borderRadius: 10,
          background: "var(--bg-card)", color: value ? "var(--text-primary)" : "var(--text-muted)",
          fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer",
          minWidth: 130,
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={13} style={{
        position: "absolute", right: 10, pointerEvents: "none",
        color: "var(--text-muted)",
      }} />
    </div>
  );
}

/* ── View Modal ──────────────────────────────────────────── */
function ViewModal({ user, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [onClose]);

  const Row = ({ label, value, mono }) => (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "10px 0", borderBottom: "1px solid var(--border)",
      gap: 12,
    }}>
      <span style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0, minWidth: 130 }}>{label}</span>
      <span style={{
        fontSize: 13, color: "var(--text-primary)", textAlign: "right",
        fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all",
      }}>
        {value ?? "—"}
      </span>
    </div>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div ref={ref} style={{
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: 18, width: "100%", maxWidth: 540,
        maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(135deg, rgba(15,61,62,0.08), transparent)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.08))",
              border: "2px solid rgba(239,68,68,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 700, color: "#dc2626",
            }}>
              {initials(user.name)}
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)", marginBottom: 2 }}>
                {user.name || "Unknown User"}
              </h3>
              <Badge variant="danger"><Trash2 size={9} style={{ marginRight: 4 }} />Deleted</Badge>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", padding: 6, borderRadius: 8,
            display: "flex", alignItems: "center",
          }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "0 24px 24px", overflowY: "auto", flex: 1 }}>
          {/* Identity */}
          <div style={{ marginTop: 20, marginBottom: 4 }}>
            <span style={{
              fontSize: 14, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.8px", color: "var(--text)",
            }}>Identity</span>
          </div>
          <Row label="User ID" value={user.userId} mono />
          <Row label="Full Name" value={user.name} />
          <Row label="Mobile" value={user.mobile} />
          <Row label="Email" value={user.email} />
          <Row label="Gender" value={user.gender} />
          <Row label="Date of Birth" value={fmtDate(user.dateOfBirth)} />
          <Row label="Birth Time" value={user.birthTime} />
          <Row label="Birth Place" value={user.birthPlace} />
          <Row label="Zodiac Sign" value={user.zodiacSign} />
          <Row label="Country" value={user.country} />
          <Row label="Language" value={user.languageCode} />

          {/* Account */}
          <div style={{ marginTop: 20, marginBottom: 4 }}>
            <span style={{
              fontSize: 14, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.8px", color: "var(--text)",
            }}>Account</span>
          </div>
          <Row label="Source" value={<SourceBadge source={user.source} />} />
          <Row label="Registration Status" value={user.registrationStatus} />
          <Row label="Registration Date" value={fmtDate(user.registrationDate)} />
          <Row label="Device Type" value={<DeviceBadge type={user.deviceType} />} />
          <Row label="Device Status" value={user.deviceStatus} />
          <Row label="Created At" value={fmtDateTime(user.createdAt)} />
          <Row label="Updated At" value={fmtDateTime(user.updatedAt)} />
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   DeletedUsers page
════════════════════════════════════════════ */
export default function DeletedUsers() {
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);    // for modal

  // Filters
  const [search, setSearch]               = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [gender, setGender]               = useState("");
  const [source, setSource]               = useState("");
  const [registrationStatus, setRegStatus] = useState("");
  const [showFilters, setShowFilters]     = useState(false);

  // Pagination
  const [page, setPage]   = useState(0);
  const [size]            = useState(20);

  /* Debounce search */
  const debounceRef = useRef(null);
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(0);
    }, 350);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size,
        sort: "createdAt",
        dir: "desc",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(gender && { gender }),
        ...(source && { source }),
        ...(registrationStatus && { registrationStatus }),
      };
      const res = await deletedUsersApi.getAll(params);
      const d = res.data.data;
      setData(d.content ?? []);
      setTotal(d.totalElements ?? 0);
      setTotalPages(d.totalPages ?? 0);
    } catch (e) {
      console.error(e);
      showToast("Failed to load deleted users", "error");
    } finally {
      setLoading(false);
    }
  }, [page, size, debouncedSearch, gender, source, registrationStatus]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setSearch(""); setDebouncedSearch("");
    setGender(""); setSource(""); setRegStatus("");
    setPage(0);
  };

  const hasFilters = debouncedSearch || gender || source || registrationStatus;

  /* ── Column config — main visible columns ── */
  const columns = [
    { key: "name",    label: "User" },
    { key: "mobile",  label: "Mobile" },
    { key: "source",  label: "Source" },
    { key: "device",  label: "Device" },
    { key: "regDate", label: "Registered" },
    { key: "deleted", label: "Deleted At" },
    { key: "actions", label: "" },
  ];

  return (
    <div>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .du-anim { animation: fadeUp 0.3s ease both; }
        .du-row { transition: background 0.13s; cursor: default; }
        .du-row:hover { background: rgba(239,68,68,0.03) !important; }
        .du-view-btn {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 12px; border-radius: 8px; font-size: 12px; font-weight: 500;
          border: 1px solid var(--border); background: transparent;
          color: var(--text-secondary); cursor: pointer; transition: all 0.14s;
          font-family: var(--font-body);
        }
        .du-view-btn:hover {
          border-color: #0F3D3E; color: #0F3D3E;
          background: rgba(15,61,62,0.06);
        }
        .du-search-wrap { position: relative; display: flex; align-items: center; }
        .du-search-wrap svg { position: absolute; left: 11px; pointer-events: none; color: var(--text-muted); }
        .du-search { padding: 9px 12px 9px 35px; border-radius: 10px; border: 1px solid var(--border);
          background: var(--bg-card); color: var(--text-primary); font-size: 13px;
          font-family: var(--font-body); width: 240px; outline: none; transition: border-color 0.15s;
        }
        .du-search:focus { border-color: #0F3D3E; }
        .du-search::placeholder { color: var(--text-muted); }
        .du-page-btn { display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--border);
          background: transparent; cursor: pointer; transition: all 0.14s; color: var(--text-secondary);
        }
        .du-page-btn:hover:not(:disabled) { border-color: #0F3D3E; color: #0F3D3E; background: rgba(15,61,62,0.06); }
        .du-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .du-filter-toggle { display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 10px; border: 1px solid var(--border);
          background: transparent; color: var(--text-secondary); cursor: pointer;
          font-size: 13px; font-weight: 500; font-family: var(--font-body); transition: all 0.14s;
        }
        .du-filter-toggle:hover, .du-filter-toggle.active {
          border-color: #0F3D3E; color: #0F3D3E; background: rgba(15,61,62,0.06);
        }
        .du-reset-btn { display: inline-flex; align-items: center; gap: 5px;
          padding: 8px 12px; border-radius: 10px; border: 1px solid rgba(239,68,68,0.3);
          background: rgba(239,68,68,0.05); color: #dc2626; cursor: pointer;
          font-size: 12px; font-weight: 500; font-family: var(--font-body); transition: all 0.14s;
        }
        .du-reset-btn:hover { background: rgba(239,68,68,0.1); }
      `}</style>

      {/* ── Page Header ── */}
      <div className="du-anim" style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))",
            border: "1px solid rgba(239,68,68,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <UserX size={18} strokeWidth={2} color="#dc2626" />
          </div>
          <div>
            <h1 style={{
              fontFamily: "var(--font-display)", fontSize: 20,
              fontWeight: 700, color: "var(--text-primary)",
            }}>
              Deleted Users
            </h1>
            <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>
              {loading ? "Loading…" : `${total.toLocaleString("en-IN")} deleted account${total !== 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="du-anim" style={{
        display: "flex", gap: 10, flexWrap: "wrap",
        alignItems: "center", marginBottom: 14,
        animationDelay: "0.04s",
      }}>
        {/* Search */}
        <div className="du-search-wrap">
          <Search size={14} strokeWidth={2} />
          <input
            className="du-search"
            placeholder="Search name, mobile, email, ID…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>

        {/* Filter toggle */}
        <button
          className={`du-filter-toggle${showFilters ? " active" : ""}`}
          onClick={() => setShowFilters(v => !v)}
        >
          <SlidersHorizontal size={14} strokeWidth={2} />
          Filters
          {hasFilters && (
            <span style={{
              display: "inline-flex", width: 18, height: 18, borderRadius: "50%",
              background: "#dc2626", color: "#fff",
              fontSize: 10, fontWeight: 700,
              alignItems: "center", justifyContent: "center",
            }}>!</span>
          )}
        </button>

        {/* Reset */}
        {hasFilters && (
          <button className="du-reset-btn" onClick={resetFilters}>
            <X size={12} /> Clear filters
          </button>
        )}

        {/* Refresh */}
        <button
          className="du-filter-toggle"
          onClick={load}
          disabled={loading}
          style={{ marginLeft: "auto" }}
        >
          <RefreshCw size={13} strokeWidth={2} style={{ ...(loading && { animation: "spin 1s linear infinite" }) }} />
          Refresh
        </button>
      </div>

      {/* ── Filter Panel ── */}
      {showFilters && (
        <div className="du-anim" style={{
          display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14,
          padding: "14px 16px",
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: 12,
        }}>
          <Select
            value={gender} onChange={v => { setGender(v); setPage(0); }}
            placeholder="All Genders"
            options={[
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
              { value: "Other", label: "Other" },
            ]}
          />
          <Select
            value={source} onChange={v => { setSource(v); setPage(0); }}
            placeholder="All Sources"
            options={[
              { value: "ASTRO", label: "ASTRO" },
              { value: "IND", label: "IND" },
            ]}
          />
          <Select
            value={registrationStatus} onChange={v => { setRegStatus(v); setPage(0); }}
            placeholder="All Reg. Status"
            options={[
              { value: "REGISTERED", label: "Registered" },
              { value: "INCOMPLETE", label: "Incomplete" },
            ]}
          />
        </div>
      )}

      {/* ── Warning Banner ── */}
      <div className="du-anim" style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px", borderRadius: 10, marginBottom: 16,
        background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)",
        animationDelay: "0.06s",
      }}>
        <AlertTriangle size={14} strokeWidth={2} color="#dc2626" />
        <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 500 }}>
          These accounts have been soft-deleted. Data is read-only.
        </span>
      </div>

      {/* ── Table ── */}
      <div className="du-anim" style={{
        background: "var(--bg-card)", border: "1px solid var(--border)",
        borderRadius: 14, overflow: "hidden", animationDelay: "0.08s",
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead>
              <tr style={{ background: "rgba(15,61,62,0.04)", borderBottom: "1px solid var(--border)" }}>
                {columns.map(col => (
                  <th key={col.key} style={{
                    padding: "11px 16px", textAlign: "left",
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.6px", color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                  }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                    {columns.map(col => (
                      <td key={col.key} style={{ padding: "14px 16px" }}>
                        <div style={{
                          height: 14, borderRadius: 6,
                          background: "var(--border)",
                          width: col.key === "name" ? 140 : col.key === "actions" ? 60 : 90,
                          animation: "pulse 1.4s ease-in-out infinite",
                          opacity: 0.6,
                        }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: "48px 24px", textAlign: "center" }}>
                    <UserX size={36} strokeWidth={1.5} color="var(--text-muted)" style={{ marginBottom: 10 }} />
                    <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No deleted users found.</p>
                  </td>
                </tr>
              ) : data.map((u, i) => (
                <tr
                  key={u.id ?? i}
                  className="du-row"
                  style={{
                    borderBottom: i < data.length - 1 ? "1px solid var(--border)" : "none",
                    background: "transparent",
                  }}
                >
                  {/* User */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                        background: "linear-gradient(135deg, rgba(239,68,68,0.12), rgba(239,68,68,0.04))",
                        border: "1px solid rgba(239,68,68,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 700, color: "#dc2626",
                      }}>
                        {initials(u.name)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                          {u.name || "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>
                          {u.userId || u.id}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Mobile */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Phone size={11} strokeWidth={2} color="var(--text-muted)" />
                      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {u.mobile || "—"}
                      </span>
                    </div>
                  </td>

                  {/* Source */}
                  <td style={{ padding: "13px 16px" }}>
                    <SourceBadge source={u.source} />
                  </td>

                  {/* Device */}
                  <td style={{ padding: "13px 16px" }}>
                    <DeviceBadge type={u.deviceType} />
                  </td>

                  {/* Registered */}
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Calendar size={11} strokeWidth={2} color="var(--text-muted)" />
                      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                        {fmtDate(u.registrationDate || u.createdAt)}
                      </span>
                    </div>
                  </td>

                  {/* Deleted At */}
                  <td style={{ padding: "13px 16px" }}>
                    <span style={{ fontSize: 13, color: "#dc2626" }}>
                      {fmtDate(u.updatedAt)}
                    </span>
                  </td>

                  {/* Actions */}
                  <td style={{ padding: "13px 16px" }}>
                    <button className="du-view-btn" onClick={() => setSelected(u)}>
                      <Eye size={12} strokeWidth={2} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 20px", borderTop: "1px solid var(--border)",
            flexWrap: "wrap", gap: 10,
          }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Page {page + 1} of {totalPages} · {total.toLocaleString("en-IN")} total
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="du-page-btn"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
              >
                <ChevronLeft size={15} />
              </button>
              <button
                className="du-page-btn"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── View Modal ── */}
      {selected && <ViewModal user={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}